import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  List as ListIcon,
  Assessment as ReviewIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Build as ToolsIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Menu as MenuIcon,
  Description as ReportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isManager, isAdmin } = useUser();

  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toolsMenuAnchorEl, setToolsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleToolsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setToolsMenuAnchorEl(event.currentTarget);
  };

  const handleToolsMenuClose = () => {
    setToolsMenuAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleToolsNavigation = (path: string) => {
    handleToolsMenuClose();
    navigate(path);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    handleDrawerClose();
  };

  const navButton = (path: string, label: string, icon: React.ReactNode) => (
    <Button
      color="inherit"
      startIcon={icon}
      onClick={() => navigate(path)}
      sx={{
        mx: 1,
        backgroundColor: location.pathname === path ? 'rgba(255,255,255,0.1)' : 'transparent',
      }}
    >
      {label}
    </Button>
  );

  const isToolsActive = location.pathname.startsWith('/admin/programs') 
    || location.pathname.startsWith('/admin/users')
    || location.pathname.startsWith('/admin/reports');

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6">Navigation</Typography>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === '/submissions'}
            onClick={() => handleMobileNavigation('/submissions')}
          >
            <ListItemIcon>
              <ListIcon />
            </ListItemIcon>
            <ListItemText primary="My Submissions" />
          </ListItemButton>
        </ListItem>

        {isManager && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/manager/review'}
              onClick={() => handleMobileNavigation('/manager/review')}
            >
              <ListItemIcon>
                <ReviewIcon />
              </ListItemIcon>
              <ListItemText primary="Manager Review" />
            </ListItemButton>
          </ListItem>
        )}

        {isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/admin/review'}
              onClick={() => handleMobileNavigation('/admin/review')}
            >
              <ListItemIcon>
                <AdminIcon />
              </ListItemIcon>
              <ListItemText primary="HR Review" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          {/* Hamburger Menu - Mobile Only */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'block', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <ReceiptIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 4 }}>
            {process.env.REACT_APP_APP_NAME || 'Expense Reimbursement'}
          </Typography>

          {/* Desktop Navigation - Hidden on Mobile */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {navButton('/submissions', 'My Submissions', <ListIcon />)}
            {isManager && navButton('/manager/review', 'Manager Review', <ReviewIcon />)}
            {isAdmin && navButton('/admin/review', 'HR Review', <AdminIcon />)}
            
            {/* Tools Menu (Admin Only - Desktop Only) */}
            {isAdmin && (
              <>
                <Button
                  color="inherit"
                  startIcon={<ToolsIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleToolsMenuOpen}
                  sx={{
                    mx: 1,
                    backgroundColor: isToolsActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  }}
                >
                  Tools
                </Button>
                <Menu
                  anchorEl={toolsMenuAnchorEl}
                  open={Boolean(toolsMenuAnchorEl)}
                  onClose={handleToolsMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem 
                    onClick={() => handleToolsNavigation('/admin/programs')}
                    selected={location.pathname === '/admin/programs'}
                  >
                    Program Management
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleToolsNavigation('/admin/users')}
                    selected={location.pathname === '/admin/users'}
                  >
                    User Management
                  </MenuItem>
                  <Divider />
                  <MenuItem 
                    onClick={() => handleToolsNavigation('/admin/reports')}
                    selected={location.pathname === '/admin/reports'}
                  >
                    <ListItemIcon>
                      <ReportIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Generate Report</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {/* Spacer for mobile - pushes user menu to right */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

          {/* User Menu - Always Visible */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.email}
            </Typography>
            <IconButton
              size="large"
              onClick={handleUserMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {user?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                Roles: {user?.roles.join(', ')}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) => theme.palette.grey[200],
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} IG Expense Reimbursement System
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;